import { API } from "@swivl-mono/api-helper";
import { IsTruthy } from "@swivl-mono/helpers";
import { BullCampaignUserJobData, BullJobType, BullQueueJobsBody, BullQueueName, LocationVarKey } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonVariant } from "@swivl-mono/ui";
import { useEffect, useMemo, useRef, useState } from "react";

import { SwivlException } from "../../../Helpers/Datadog/Datadog";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import ModalModel from "../../../Models/Modal/Modal.Model";
import NotificationsModel from "../../../Models/Notifications/Notifications.Model";
import TenantsModel from "../../../Models/Tenants/Tenants.Model";
import LocationVarsModel from "../../../Models/Variables/LocationVars.Model";
import { QuestsDropdown } from "../../Dropdowns/QuestsDropdown/QuestsDropdown";
import { SelectableList } from "../../SelectableList/SelectableList.Component";
import { TagInput, TagInputType } from "../../TagInput/TagInput";
import "./BroadcastModal.scss";

interface ListItem {
  value: string;
  title: string;
  hoverText?: string;
  data?: any;
  disabled?: boolean;
  onClick?: () => void;
}

interface BroadcastModalProps {
  preselectedLocationIds?: number[];
  preselectedTenantIds?: number[];
}

export const BroadcastModal = (props: BroadcastModalProps) => {
  const { preselectedLocationIds = [], preselectedTenantIds = [] } = props;
  const { broadcastTenants, isBroadcastTenantsLoading } = TenantsModel.useState((s) => s);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>(preselectedLocationIds);
  const [messageText, setMessageText] = useState("");
  const [messageLength, setMessageLength] = useState(0);
  const limitToastActiveRef = useRef(false);
  const [step, setStep] = useState(0);
  const [selectedQuest, setSelectedQuest] = useState<string>("after_broadcast");
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>(preselectedTenantIds);
  const [isSendingUsersToProcess, setIsSendingUsersToProcess] = useState(false);
  const { locationsWithVariables } = LocationsModel.useState((s) => s);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
    LocationVarsModel.loadAllVariables(false);
  }, []);

  const formattedTenants = useMemo(() => {
    const formattedTenants: ListItem[] =
      broadcastTenants?.map((tenant) => ({
        value: tenant.id.toString(),
        title: tenant.name || "Unknown Tenant",
        hoverText: tenant.phone || undefined,
        disabled: tenant.smsOptIn === false
      })) || [];

    return formattedTenants;
  }, [broadcastTenants]);

  const formattedLocations = useMemo(() => {
    if (!locationsWithVariables || Object.keys(locationsWithVariables).length === 0) {
      return [];
    }
    const formatted = Object.values(locationsWithVariables)
      .filter(({ variables }) => {
        return IsTruthy(variables[LocationVarKey.HasSMSCampaigns]?.value);
      })
      .map(({ location, variables }) => ({
        value: String(location.id),
        title: variables[LocationVarKey.Name]?.value || "Unknown Location",
        hoverText: variables[LocationVarKey.LocationNumber]?.value || "",
        data: variables
      }));

    return formatted;
  }, [locationsWithVariables, selectedLocationIds]);

  const isLocationsLoading = !formattedLocations || formattedLocations?.length === 0;

  useEffect(() => {
    if (step === 1 && selectedLocationIds?.length) {
      TenantsModel.getUniqueBroadcastTenantsBy(selectedLocationIds, { active: true }, true);
    }
  }, [step, selectedLocationIds]);

  /**
   * auto-select tenants that match preselected ids when broadcasttenants loads
   * this handles the case where user ids are passed as preselected tenantids from crm view
   *  */
  useEffect(() => {
    if (step === 1 && broadcastTenants && broadcastTenants.length > 0 && preselectedTenantIds.length > 0) {
      const matchingTenantIds: number[] = [];

      broadcastTenants.forEach((tenant) => {
        const users = Array.isArray(tenant.user) ? tenant.user : [tenant.user];
        const userIds = users.filter((user) => user.id).map((user) => user.id);

        // check if any of the preselected ids match either the tenantid or any user ids
        const hasMatch = preselectedTenantIds.some((preselectedId) => preselectedId === tenant.id || userIds.includes(preselectedId));

        // only auto-select if the tenant has sms opt-in enabled (not disabled)
        if (hasMatch && tenant.smsOptIn !== false) {
          matchingTenantIds.push(tenant.id);
        }
      });

      if (matchingTenantIds.length > 0) {
        setSelectedTenantIds(matchingTenantIds);
      }
    }
  }, [step, broadcastTenants, preselectedTenantIds]);

  const handleOnSendProcessUsers = async () => {
    setIsSendingUsersToProcess(true);

    const selectedUsers = broadcastTenants
      ?.filter((tenant) => selectedTenantIds?.includes(Number(tenant.id)))
      .flatMap((tenant) => {
        const users = Array.isArray(tenant.user) ? tenant.user : [tenant.user];
        return users.map((user) => ({
          user,
          locationId: tenant.locationId
        }));
      })
      .filter(Boolean) // we must remove any items that have no user
      .filter((userObject) => userObject.user);

    const uniqueUsersList = Array.from(
      new Map(
        selectedUsers?.map((userObject) => {
          if (!userObject.user) {
            console.warn("Missing user", userObject);
          }
          return [userObject.user.id, { user: userObject.user, locationId: userObject.locationId }];
        })
      ).values()
    );

    const jobs: { name: string; data: BullCampaignUserJobData; options: any }[] =
      uniqueUsersList.map((userLocationObject) => {
        return {
          name: BullJobType.CampaignUsers,
          data: {
            user: userLocationObject.user,
            messageText: messageText,
            questSlug: selectedQuest,
            agentMemberId: Member?.id,
            locationId: userLocationObject.locationId
          },
          options: {
            removeOnComplete: {
              age: 172800, // 2 days
              count: 3000
            },
            removeOnFail: {
              count: 1000
            }
          }
        };
      }) || [];

    const bullQueueJobsBody: BullQueueJobsBody = [
      {
        queueName: BullQueueName.CampaignUsers,
        jobs: jobs
      }
    ];

    try {
      await API.Bull.queueJobs(bullQueueJobsBody);
      handleOnCancel();
    } catch (error) {
      const errorToCapture = error instanceof Error ? error : new Error(String(error));
      SwivlException.capture(errorToCapture, "BroadcastModal.handleOnSendProcessUsers");
      NotificationsModel.createToastNotification({
        message: "Could not send broadcast. Please try again.",
        title: "Send failed"
      });
    } finally {
      setIsSendingUsersToProcess(false);
    }
  };

  const handleOnCancel = () => {
    ModalModel.hideModal();
  };

  const handleLocationItemSelected = (value: string) => {
    const selectedLocations: number[] = value?.length ? value.split(",").map(Number) : [];
    setSelectedLocationIds(selectedLocations);
  };

  const handleTenantItemSelected = (value: string) => {
    const selectedTenants: number[] = value?.length ? value.split(",").map(Number) : [];
    setSelectedTenantIds(selectedTenants);
  };

  const handleOnBack = () => {
    if (step === 1) {
      clearSelectedTenantIds();
    }
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleOnNext = () => {
    setStep(step + 1);
  };

  const clearSelectedTenantIds = () => {
    setSelectedTenantIds([]);
  };

  return (
    <div className="BroadcastModal">
      {step === 0 && (
        <>
          <label>SELECT LOCATION(S)</label>
          <div className="BroadcastStepContainer">
            <SelectableList
              open={true}
              title="Select Locations"
              openHeight={300}
              items={formattedLocations}
              selectedItemValue={selectedLocationIds.join(",")}
              onItemSelected={handleLocationItemSelected}
              showCheckboxes={true}
              showSelectAll={true}
              isLoading={isLocationsLoading}
              loadingText1={"Loading Locations..."}
              showSearch={true}
              useWindowing={true}
              windowHeight={200}
              windowWidth={477}
              windowItemHeight={26}
            />
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <label>SELECT CUSTOMER(S)</label>
          <div className="BroadcastStepContainer">
            <SelectableList
              open={true}
              title="Select Users"
              openHeight={300}
              items={formattedTenants || undefined}
              selectedItemValue={selectedTenantIds.join(",")}
              onItemSelected={handleTenantItemSelected}
              showCheckboxes={true}
              showSelectAll={true}
              isLoading={isBroadcastTenantsLoading}
              loadingText1={"Loading Customer(s)..."}
              showSearch={true}
              useWindowing={true}
              windowHeight={200}
              windowWidth={477}
              windowItemHeight={26}
            />
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="BroadcastStepContainer">
            <div className="BroadcastTextArea">
              <label>MESSAGE</label>
              <TagInput
                value={messageText}
                onChange={(e) => setMessageText(e.target.value ?? "")}
                maxLength={240}
                onLengthChange={setMessageLength}
                onMaxLengthExceeded={() => {
                  if (limitToastActiveRef.current) return;
                  limitToastActiveRef.current = true;
                  void NotificationsModel.createToastNotification({
                    message: "Message cannot exceed 240 characters.",
                    title: "Character limit reached",
                    autoDismissMs: 3000
                  });
                  setTimeout(() => {
                    limitToastActiveRef.current = false;
                  }, 3000);
                }}
                helperText="To add a variable, type #"
              />
              <span className={messageLength >= 240 ? "textMax" : ""}>{messageLength}/240</span>
            </div>
            <QuestsDropdown
              label="Follow On Action"
              name="questSlug"
              value={selectedQuest}
              questSelected={(slug) => {
                setSelectedQuest(slug);
              }}
              //Broadcast Messaging journey: 1.7, 2.1, test-journey
              filterJourney={(journey) => journey?.slug === "YZPFA4Xv2I" || journey?.slug === "k43o9hJGTB" || journey?.slug === "gSnWZZPMB9"}
            />
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className="BroadcastStepContainer">
            <div className="BroadcastPreview">
              <div className="previewTitle">Here's what you're sending:</div>
              <div className="message">
                <div className="messageTitle">Message:</div>
                <div className="messageContent">
                  <TagInput value={messageText} type={TagInputType.NoBorder} readOnly />
                </div>
              </div>
              <div className="audience">
                <div className="audienceTitle">Audience:</div>
                <div className="audienceContent">{`${selectedTenantIds?.length} customer(s) across ${selectedLocationIds?.length} location(s)`}</div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="footer">
        <div className="buttons">
          {step > 0 && (
            <Button variant={ButtonVariant.GHOST} onClick={handleOnBack}>
              Back
            </Button>
          )}

          {step == 0 && (
            <>
              <Button variant={ButtonVariant.GHOST} onClick={handleOnCancel}>
                Cancel
              </Button>

              <Button color={ButtonColor.ORANGE} onClick={handleOnNext} disabled={selectedLocationIds.length === 0}>
                Next
              </Button>
            </>
          )}

          {step == 1 && (
            <Button color={ButtonColor.ORANGE} onClick={handleOnNext} disabled={selectedTenantIds.length === 0}>
              Next
            </Button>
          )}

          {step == 2 && (
            <Button color={ButtonColor.ORANGE} onClick={handleOnNext} disabled={!selectedQuest || !messageText}>
              Next
            </Button>
          )}

          {step === 3 && (
            <Button
              color={ButtonColor.ORANGE}
              onClick={handleOnSendProcessUsers}
              disabled={!messageText || !selectedQuest || !selectedTenantIds}
              loading={isSendingUsersToProcess}
            >
              Send Now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
